
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileSearch, Save, Loader2 } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogDescription
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useGeminiApi } from "@/hooks/useGeminiApi";
import { supabase } from "@/integrations/supabase/client";
import { Document } from "./DocumentTable";
import { prepareDocumentForAI, isAnalysisSupported } from "@/utils/documentParser";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";

interface ProcurementDocumentAnalysisProps {
  document: Document;
  tenderId: string;
}

export const ProcurementDocumentAnalysis = ({ document, tenderId }: ProcurementDocumentAnalysisProps) => {
  const { t, i18n } = useTranslation('offerManagement');
  const [isOpen, setIsOpen] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { generateContent, isLoading: isGenerating } = useGeminiApi();
  const { toast } = useToast();
  const currentLanguage = i18n.language;

  const analyzeDocument = async () => {
    setIsAnalyzing(true);
    setError(null);
    
    try {
      const { data, error: downloadError } = await supabase.storage
        .from('tender_documents')
        .download(document.file_path);
      
      if (downloadError) {
        throw new Error(`Error downloading document: ${downloadError.message}`);
      }

      const arrayBuffer = await data.arrayBuffer();
      
      const { data: processedData, mimeType: processedMimeType } = 
        await prepareDocumentForAI(arrayBuffer, document.file_type);

      let systemInstruction = "";
      
      if (currentLanguage === "da") {
        systemInstruction = 
          "Du er en professionel udbudsanalytiker. " +
          "Analysér udbudsdokumentet og skab en struktureret og professionel analyse med følgende afsnit: " +
          "1. Projektbeskrivelse " +
          "2. Egnethedskriterier " +
          "3. Krav til indlevering " +
          "4. Tidsfrister " +
          "5. Evalueringskriterier " +
          "6. Tekniske Specifikationer " +
          "7. Potentielle advarselstegn " +
          "8. Centrale handlinger for tilbudsgivere " +
          "Start direkte med analysen uden indledende eller afsluttende bemærkninger. " +
          "Brug en professionel, objektiv tone uden dialogform. " +
          "Sørg for KLAR formatering med god afstand mellem afsnit og punkter. " +
          "Hold hvert punkt i listeform kort og præcist.";
      } else {
        systemInstruction = 
          "You are a professional procurement analyst. " +
          "Analyze the procurement document and create a structured, professional analysis with the following sections: " +
          "1. Project Overview " +
          "2. Eligibility Criteria " +
          "3. Submission Requirements " +
          "4. Deadlines " +
          "5. Evaluation Criteria " +
          "6. Technical Specifications " +
          "7. Potential Red Flags " +
          "8. Key Actions for Bidders " +
          "Begin directly with the analysis without any introductory or concluding remarks. " +
          "Use a professional, objective tone without dialogue format. " +
          "Ensure CLEAR formatting with proper spacing between sections and bullet points. " +
          "Keep each bullet point item short and concise.";
      }

      let prompt = currentLanguage === "da" 
        ? `Analysér venligst dette udbudsdokument og giv en klar, struktureret sammenfatning til potentielle tilbudsgivere med følgende afsnit:

1. **Projektbeskrivelse**
   - Hvad er der udbudt?
   - Kort beskrivelse af projektets omfang

2. **Egnethedskriterier**
   - Krav til juridisk status
   - Krav til økonomisk kapacitet
   - Krav til erfaring
   - Krav til teknisk kapacitet
   - Andre egnethedskrav

3. **Krav til indlevering**
   - Påkrævede dokumenter
   - Formatkrav
   - Antal kopier
   - Sprogkrav
   - Indleveringsmetode
   - Adresse for indlevering

4. **Tidsfrister**
   - Frist for spørgsmål/afklaringer
   - Indleveringsfrist
   - Vedståelsesperiode

5. **Evalueringskriterier**
   - Tekniske evalueringskriterier og vægtning
   - Økonomiske evalueringskriterier og vægtning
   - Andre evalueringskriterier
   - Samlet vægtningssystem

6. **Tekniske Specifikationer**
   - Centrale krav til ydelsen
   - Overensstemmelseskrav
   - Garanti- og supportvilkår

7. **Potentielle advarselstegn**
   - Eventuel tvetydig formulering
   - Urealistiske tidsfrister
   - Byrdefulde vilkår og betingelser
   - Manglende information
   - Særlige klausuler, som tilbudsgivere bør være opmærksomme på

8. **Centrale handlinger for tilbudsgivere**
   - Vigtige trin og anbefalinger

VIGTIGT:
- Start IKKE analysen med introduktioner som "Jeg vil analysere".
- Afslut IKKE analysen med "Held og lykke" eller lignende fraser.
- Brug IKKE formuleringer som "baseret på de leverede dokumenter".
- Sørg for KLAR formatering med god afstand mellem afsnit og punkter.
- Hold hvert punkt i listeform kort og præcist.

Formatér din analyse i klart markdown med overskrifter og punktopstillinger. Hvis information til et afsnit ikke er tilgængelig i dokumentet, angiv dette tydeligt.`
        : `Please analyze this procurement document and provide a clear, structured summary for potential bidders with the following sections:

1. **Project Overview**
   - What is being procured?
   - Brief description of the project scope

2. **Eligibility Criteria**
   - Legal status requirements
   - Financial stability requirements
   - Experience requirements
   - Technical capacity requirements
   - Other eligibility requirements

3. **Submission Requirements**
   - Required documents
   - Format requirements
   - Number of copies
   - Language requirements
   - Submission method
   - Address for submission

4. **Deadlines**
   - Clarification deadline
   - Submission deadline
   - Validity period

5. **Evaluation Criteria**
   - Technical evaluation criteria and weights
   - Financial evaluation criteria and weights
   - Other evaluation criteria
   - Overall weighting system

6. **Technical Specifications**
   - Key performance requirements
   - Compliance requirements
   - Warranty and support terms

7. **Potential Red Flags**
   - Any ambiguous language
   - Unrealistic deadlines
   - Onerous terms and conditions
   - Missing information
   - Special clauses bidders should pay attention to

8. **Key Actions for Bidders**
   - Important steps and recommendations

IMPORTANT:
- DO NOT start the analysis with introductions like "I will analyze".
- DO NOT end the analysis with "Good luck" or similar phrases.
- DO NOT use phrases like "based on the provided documents".
- Ensure CLEAR formatting with proper spacing between sections and bullet points.
- Keep each bullet point item short and concise.

Format your analysis in clear markdown with headings and bullet points. If information for any section is not available in the document, please indicate this clearly.`;
      
      if (document.file_type !== processedMimeType) {
        prompt += currentLanguage === "da"
          ? `\n\nBemærk: Dette dokument var oprindeligt i ${document.file_type} format og er blevet konverteret til tekst for bedre analyse. Der kan være tab af formatering eller andre konverteringsartefakter. Hvis du bemærker huller eller uoverensstemmelser i informationen, bør du nævne dette i din analyse.`
          : `\n\nNote: This document was originally in ${document.file_type} format and has been converted to text for better analysis. There may be loss of formatting or other conversion artifacts. If you notice gaps or inconsistencies in the information, please mention this in your analysis.`;
      }
      
      console.log(`Analyzing document: ${document.file_name} (Original type: ${document.file_type}, Processed type: ${processedMimeType})`);
      console.log(`Document size: ${document.file_size} bytes`);

      const response = await generateContent(prompt, {
        model: "gemini-2.0-flash",
        systemInstruction,
        temperature: 0.2,
        fileData: [{
          data: processedData,
          mimeType: processedMimeType
        }]
      });

      if (response && response.generatedText) {
        setAnalysis(response.generatedText);
      } else {
        throw new Error(currentLanguage === "da" 
          ? "Kunne ikke generere dokumentanalyse" 
          : "Failed to generate document analysis");
      }
    } catch (err: any) {
      console.error("Error analyzing document:", err);
      setError(err.message || (currentLanguage === "da" 
        ? "Der opstod en fejl under analyse af dokumentet" 
        : "An error occurred while analyzing the document"));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const saveAnalysisAsText = () => {
    if (!analysis) return;
    
    // Create a clean version without markdown syntax
    const cleanText = analysis
      .replace(/## /g, '') // Remove markdown headers
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/- /g, '• '); // Replace markdown lists with bullet points
    
    // Create a blob with the analysis text
    const blob = new Blob([cleanText], { type: 'text/plain;charset=utf-8' });
    
    // Create a download link
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a'); // Use window.document instead of document
    a.href = url;
    
    // Create a filename based on the document name
    const fileName = document.file_name.replace(/\.[^/.]+$/, '') + '_analysis.txt';
    a.download = fileName;
    
    // Trigger the download
    window.document.body.appendChild(a); // Use window.document instead of document
    a.click();
    
    // Clean up
    URL.revokeObjectURL(url);
    window.document.body.removeChild(a); // Use window.document instead of document
    
    toast({
      title: currentLanguage === 'da' ? 'Analysen er gemt' : 'Analysis saved',
      description: currentLanguage === 'da' 
        ? `Analysen er blevet gemt som ${fileName}` 
        : `Analysis has been saved as ${fileName}`,
    });
  };

  function renderAnalysisContent() {
    if (isAnalyzing) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <LoadingSpinner size={32} className="text-primary mb-4" />
          <p className="text-muted-foreground">
            {currentLanguage === 'da' ? "Analyserer dokumenter..." : "Analyzing documents..."}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {currentLanguage === 'da' ? "Dette kan tage et øjeblik..." : "This may take a moment..."}
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>{currentLanguage === 'da' ? 'Analyse Fejlet' : 'Analysis Failed'}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }

    if (!analysis) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileSearch className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {currentLanguage === 'da' ? 'Udbudsanalyse' : 'Procurement Document Analysis'}
          </h3>
          <p className="text-muted-foreground mb-6">
            {currentLanguage === 'da' 
              ? 'Generer en omfattende analyse af dette dokument i letforståeligt sprog, med fokus på nøgleinformation for tilbudsgivere.'
              : 'Generate a comprehensive analysis of this document in easy-to-understand language, highlighting key information for bidders.'}
          </p>
          <Button 
            onClick={analyzeDocument} 
            className="flex items-center gap-2"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {currentLanguage === 'da' ? "Analyserer..." : "Analyzing..."}
              </>
            ) : (
              <>{currentLanguage === 'da' ? 'Analyser Dokument' : 'Analyze Document'}</>
            )}
          </Button>
        </div>
      );
    }

    const sections = analysis.split(/^## /m).filter(Boolean);
    
    return (
      <div className="space-y-4">
        {analysis && (
          <div className="flex justify-end mb-4">
            <Button 
              variant="outline" 
              onClick={saveAnalysisAsText}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {currentLanguage === 'da' ? 'Gem som tekst' : 'Save as text'}
            </Button>
          </div>
        )}
        
        {sections.length <= 1 ? (
          <Card className="p-6 whitespace-pre-wrap">
            <div dangerouslySetInnerHTML={{ __html: analysis.replace(/\n/g, '<br />') }} />
          </Card>
        ) : (
          <Accordion type="multiple" defaultValue={['executive-summary']} className="w-full">
            {sections.map((section, index) => {
              const lines = section.split('\n');
              const title = index === 0 
                ? (currentLanguage === 'da' ? 'Sammenfatning' : 'Executive Summary') 
                : lines[0].trim();
              const content = index === 0 ? section : lines.slice(1).join('\n');
              const itemId = title.toLowerCase().replace(/\s+/g, '-');
              
              return (
                <AccordionItem value={itemId} key={itemId}>
                  <AccordionTrigger className="text-left">
                    {title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="prose max-w-none text-sm space-y-3 py-2">
                      {content.split('\n\n').map((paragraph, i) => (
                        <div key={i} className="mb-4">
                          {paragraph.startsWith('- ') ? (
                            <ul className="list-disc pl-6 space-y-2 my-3">
                              {paragraph.split('\n- ').map((item, j) => (
                                <li key={j} className="mb-2">{item.replace('- ', '')}</li>
                              ))}
                            </ul>
                          ) : paragraph.startsWith('1. ') ? (
                            <ol className="list-decimal pl-6 space-y-2 my-3">
                              {paragraph.split('\n').map((item, j) => (
                                <li key={j} className="mb-2">{item.replace(/^\d+\.\s/, '')}</li>
                              ))}
                            </ol>
                          ) : paragraph.startsWith('### ') ? (
                            <h4 className="text-base font-medium mt-6 mb-2">{paragraph.replace('### ', '')}</h4>
                          ) : (
                            <p className="my-2">{paragraph}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    );
  }

  const handleOpenDialog = () => {
    setIsOpen(true);
    
    if (!analysis && !isAnalyzing && isAnalysisSupported(document.file_type)) {
      analyzeDocument();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button 
                variant="ghost" 
                size="icon" 
                disabled={!isAnalysisSupported(document.file_type)}
                onClick={handleOpenDialog}
              >
                <FileSearch className="h-4 w-4" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {isAnalysisSupported(document.file_type) 
              ? t('documents.analyzeDocument')
              : currentLanguage === 'da' 
                ? 'Dette filformat understøttes ikke. Konverter venligst til PDF eller brug et understøttet billedformat (PNG, JPEG, WEBP).'
                : 'This file format is not supported. Please convert to PDF or use a supported image format (PNG, JPEG, WEBP).'
            }
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DialogContent className="max-w-3xl h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5" />
            {currentLanguage === 'da' ? 'Analyser dokument' : 'Analyze Document'}
          </DialogTitle>
          <DialogDescription>
            {currentLanguage === 'da' ? 'Dokument:' : 'Document:'} {document.file_name}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-full pr-4">
          {renderAnalysisContent()}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
